import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Brain,
  MessageSquare,
  Settings2,
  LineChart,
  Users2,
  Workflow,
  ArrowRight,
  Database,
  Network,
  Shield,
  Clock
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { animations } from '@/lib/animations';
import { ROUTES } from '@/lib/constants';
import { translations } from '@/lib/translations';
import { useLanguage } from '@/contexts/LanguageContext';

const Product = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language];

  const capabilities = [
    {
      icon: <Brain className="w-6 h-6 text-primary" />,
      title: t.product.capabilities.items.ai.title,
      description: t.product.capabilities.items.ai.description
    },
    {
      icon: <MessageSquare className="w-6 h-6 text-primary" />,
      title: t.product.capabilities.items.integration.title,
      description: t.product.capabilities.items.integration.description
    },
    {
      icon: <Settings2 className="w-6 h-6 text-primary" />,
      title: t.product.capabilities.items.customization.title,
      description: t.product.capabilities.items.customization.description
    },
    {
      icon: <LineChart className="w-6 h-6 text-primary" />,
      title: t.product.capabilities.items.analytics.title,
      description: t.product.capabilities.items.analytics.description
    }
  ];

  const benefits = [
    {
      icon: <Clock className="w-6 h-6 text-primary" />,
      title: t.product.benefits.items.support.title,
      description: t.product.benefits.items.support.description
    },
    {
      icon: <Users2 className="w-6 h-6 text-primary" />,
      title: t.product.benefits.items.scalability.title,
      description: t.product.benefits.items.scalability.description
    },
    {
      icon: <Workflow className="w-6 h-6 text-primary" />,
      title: t.product.benefits.items.automation.title,
      description: t.product.benefits.items.automation.description
    },
    {
      icon: <Shield className="w-6 h-6 text-primary" />,
      title: t.product.benefits.items.security.title,
      description: t.product.benefits.items.security.description
    }
  ];

  const techFeatures = [
    {
      icon: <Brain className="w-6 h-6 text-primary" />,
      title: t.product.technology.items.nlp.title,
      description: t.product.technology.items.nlp.description
    },
    {
      icon: <Database className="w-6 h-6 text-primary" />,
      title: t.product.technology.items.data.title,
      description: t.product.technology.items.data.description
    },
    {
      icon: <Network className="w-6 h-6 text-primary" />,
      title: t.product.technology.items.api.title,
      description: t.product.technology.items.api.description
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="flex flex-col items-center justify-center min-h-[70vh] pb-8 pt-6 md:pt-10 md:pb-12 lg:pt-16 lg:pb-24 relative overflow-hidden w-full">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent" />
        <div className="relative mx-auto flex max-w-[980px] flex-col items-center gap-8 text-center px-4">
          <h1 className={`text-4xl font-bold leading-tight tracking-tighter md:text-7xl lg:leading-[1.1] bg-gradient-to-r from-primary via-primary/80 to-primary/50 bg-clip-text text-transparent ${animations.fadeInSlideUp}`}>
            {t.product.hero.title}
          </h1>
          <p className={`max-w-[750px] text-xl text-muted-foreground sm:text-2xl ${animations.fadeInSlideUp} ${animations.delay200}`}>
            {t.product.hero.subtitle}
          </p>
          <div className={`flex flex-col gap-4 sm:flex-row sm:gap-6 ${animations.fadeInSlideUp} ${animations.delay300}`}>
            <Button size="lg" className="group text-lg px-8 py-6" onClick={() => navigate(ROUTES.START_POC)}>
              {t.product.cta.startPOC}
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className={`w-full py-16 md:py-24 lg:py-32 bg-muted/30 ${animations.fadeIn}`}>
        <div className="mx-auto flex max-w-[1100px] flex-col items-center gap-12 px-4">
          <div className="text-center">
            <h2 className={`text-2xl font-bold leading-tight tracking-tighter md:text-4xl text-center ${animations.fadeInSlideUp}`}>
              {t.product.capabilities.title}
            </h2>
            <p className={`max-w-[750px] text-lg text-muted-foreground/80 md:text-xl text-center ${animations.fadeInSlideUp} ${animations.delay200}`}>
              {t.product.capabilities.subtitle}
            </p>
          </div>
          <div className={`grid gap-8 sm:grid-cols-2 lg:grid-cols-4 ${animations.fadeInSlideUp} ${animations.delay200}`}>
            {capabilities.map((capability, index) => (
              <Card
                key={index}
                className={`p-6 border-primary/10 hover:shadow-lg transition-all ${animations.fadeInSlideUp}`}
                style={{ animationDelay: `${(index + 1) * 100}ms` }}
              >
                <div className="flex flex-col gap-4">
                  <div className="p-2 bg-primary/10 rounded-lg w-fit">
                    {capability.icon}
                  </div>
                  <h3 className="text-xl font-bold">{capability.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{capability.description}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className={`w-full py-16 md:py-24 lg:py-32 ${animations.fadeIn}`}>
        <div className="mx-auto flex max-w-[1100px] flex-col items-center gap-12 px-4">
          <div className="text-center">
            <h2 className={`text-2xl font-bold leading-tight tracking-tighter md:text-4xl text-center ${animations.fadeInSlideUp}`}>
              {t.product.benefits.title}
            </h2>
            <p className={`max-w-[750px] text-lg text-muted-foreground/80 md:text-xl text-center ${animations.fadeInSlideUp} ${animations.delay200}`}>
              {t.product.benefits.subtitle}
            </p>
          </div>
          <div className={`grid gap-8 sm:grid-cols-2 lg:grid-cols-4 ${animations.fadeInSlideUp} ${animations.delay200}`}>
            {benefits.map((benefit, index) => (
              <Card
                key={index}
                className={`p-6 border-primary/10 hover:shadow-lg transition-all ${animations.fadeInSlideUp}`}
                style={{ animationDelay: `${(index + 1) * 100}ms` }}
              >
                <div className="flex flex-col gap-4">
                  <div className="p-2 bg-primary/10 rounded-lg w-fit">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-bold">{benefit.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className={`w-full py-16 md:py-24 lg:py-32 bg-muted/30 ${animations.fadeIn}`}>
        <div className="mx-auto flex max-w-[1100px] flex-col items-center gap-12 px-4">
          <div className="text-center">
            <h2 className={`text-2xl font-bold leading-tight tracking-tighter md:text-4xl text-center ${animations.fadeInSlideUp}`}>
              {t.product.technology.title}
            </h2>
            <p className={`max-w-[750px] text-lg text-muted-foreground/80 md:text-xl text-center ${animations.fadeInSlideUp} ${animations.delay200}`}>
              {t.product.technology.subtitle}
            </p>
          </div>
          <div className={`grid gap-8 sm:grid-cols-2 lg:grid-cols-3 ${animations.fadeInSlideUp} ${animations.delay200}`}>
            {techFeatures.map((tech, index) => (
              <Card
                key={index}
                className={`p-6 border-primary/10 hover:shadow-lg transition-all ${animations.fadeInSlideUp}`}
                style={{ animationDelay: `${(index + 1) * 100}ms` }}
              >
                <div className="flex flex-col gap-4">
                  <div className="p-2 bg-primary/10 rounded-lg w-fit">
                    {tech.icon}
                  </div>
                  <h3 className="text-xl font-bold">{tech.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{tech.description}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`w-full py-16 md:py-24 lg:py-32 ${animations.fadeIn}`}>
        <div className="mx-auto flex max-w-[980px] flex-col items-center gap-8 text-center px-4">
          <h2 className={`text-2xl font-bold leading-tight tracking-tighter md:text-4xl text-center ${animations.fadeInSlideUp}`}>
            {t.product.cta.title}
          </h2>
          <p className={`max-w-[750px] text-lg text-muted-foreground/80 md:text-xl text-center ${animations.fadeInSlideUp} ${animations.delay200}`}>
            {t.product.cta.description}
          </p>
          <div className={`flex flex-col gap-4 sm:flex-row sm:gap-6 ${animations.fadeInSlideUp} ${animations.delay300}`}>
            <Button size="lg" className="group text-lg px-8 py-6" onClick={() => navigate(ROUTES.START_POC)}>
              {t.product.cta.startPOC}
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button size="lg" variant="outline" className="text-lg px-8 py-6" onClick={() => navigate(ROUTES.CONTACT_SALES)}>
              {t.product.cta.talkExpert}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Product; 